import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthProvider";
import { useProfile } from "@/context/ProfileProvider";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const profileSchema = z.object({
  first_name: z.string().min(1, "El nombre es obligatorio"),
  last_name: z.string().min(1, "El apellido es obligatorio"),
});

const orgSchema = z.object({
  orgName: z.string().min(1, "El nombre de la organización es obligatorio"),
});

const passwordSchema = z
  .object({
    newPassword: z.string().min(6, "Mínimo 6 caracteres"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });

export default function ProfileSettings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"perfil" | "cuenta" | "mispagos">(
    "perfil"
  );
  const [email, setEmail] = useState("");
  // const selectedOrgId = localStorage.getItem("selectedOrgId");
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<any[]>([]);

  const profileForm = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
    },
  });

  const orgForm = useForm({
    resolver: zodResolver(orgSchema),
    defaultValues: {
      orgName: "",
    },
  });

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    if (user?.id) {
      loadProfile();
      // loadOrg();
      fetchPayments();
      setEmail(user.email);
    }
  }, [user?.id]);

  const loadProfile = async () => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    if (data) {
      profileForm.reset({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
      });
    }
  };

  const onSubmitProfile = async (values: any) => {
    const { error } = await supabase
      .from("profiles")
      .update(values)
      .eq("id", user.id);

    if (error) toast.error("Error al actualizar perfil");
    else toast.success("Perfil actualizado");
  };

  const onSubmitPassword = async (values: any) => {
    const { error } = await supabase.auth.updateUser({
      password: values.newPassword,
    });

    if (error) toast.error("Error al cambiar contraseña");
    else {
      toast.success("Contraseña actualizada");
      passwordForm.reset();
    }
  };

  async function fetchPayments() {
    setLoading(true);
    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .order("created_at", { ascending: false });

    if (!error) setPayments(data || []);
    setLoading(false);
  }

  const tabs = [
    { id: "perfil", label: "Perfil" },
    { id: "mispagos", label: "Mis pagos" },
    { id: "cuenta", label: "Cambiar contraseña" },
  ];

  return (
    <div className="flex flex-col md:flex-row max-w-5xl mx-auto py-10 px-4 gap-8 w-full">
      {/* Sidebar */}
      <aside className="w-full md:w-1/4 border rounded-lg p-4 ">
        <nav className="space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                "w-full text-left px-3 py-2 rounded-md text-sm font-medium",
                activeTab === tab.id && "bg-accent "
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <main className="w-full md:w-3/4 space-y-6 ">
        {activeTab === "perfil" && (
          <Form {...profileForm}>
            <form
              onSubmit={profileForm.handleSubmit(onSubmitProfile)}
              className="space-y-4"
            >
              <h2 className="text-2xl font-semibold">
                Configuración de perfil
              </h2>

              <div>
                <Label className="mb-3">Correo electrónico</Label>
                <Input value={email} disabled />
              </div>

              <FormField
                control={profileForm.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre</FormLabel>
                    <FormControl>
                      <Input placeholder="Tu nombre" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={profileForm.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apellido</FormLabel>
                    <FormControl>
                      <Input placeholder="Tu apellido" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit">Guardar cambios</Button>
            </form>
          </Form>
        )}
        {activeTab === "mispagos" && (
          <>
            <h2 className="text-2xl font-semibold">Mis pagos</h2>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead>Monto</TableHead>
                    <TableHead>Frecuencia</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>F. Inicio</TableHead>
                    <TableHead>F. Fin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={6}>
                          <Skeleton className="h-6 w-full" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : payments.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center py-4 text-muted-foreground"
                      >
                        No tienes pagos registrados.
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.plan_name}</TableCell>
                        <TableCell>S/ {p.amount.toFixed(2)}</TableCell>
                        <TableCell className="capitalize">
                          {p.frequency_type}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              p.status === "paid" ? "default" : "secondary"
                            }
                          >
                            {p.status === "paid" ? "Pagado" : "Pendiente"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(p.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(p.next_payment_date).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        {activeTab === "cuenta" && (
          <>
            {/* Contraseña */}
            <Form {...passwordForm}>
              <form
                onSubmit={passwordForm.handleSubmit(onSubmitPassword)}
                className="space-y-4 pt-6"
              >
                <h2 className="text-2xl font-semibold">Cambiar contraseña</h2>

                <FormField
                  control={passwordForm.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nueva contraseña</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={passwordForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmar contraseña</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit">Guardar contraseña</Button>
              </form>
            </Form>
          </>
        )}
      </main>
    </div>
  );
}
